/* ============================================================================
   ALIA · GOLDEN HOUR — 03-golden-hour.js
   ----------------------------------------------------------------------------
   Every line of JavaScript for this theme, in ONE file, loaded with defer.
   Asset name is taken from the developer guide, which specifies
   "assets/03-golden-hour.js" (no leading r).

   Structure:
     REG        per-section registry of ScrollTriggers, tweens and intervals
     buildApp() the original motion system, byte-identical inside
     buildDock()/buildMap()  the two small feature scripts
     boot()     runs all three and records what they created
     teardown() kills what boot() created
     lifecycle  shopify:section:load / :unload

   ONE Lenis instance, ONE ScrollTrigger registry, spanning every section.
   The JS is never fragmented per section — sections are markup only.
   ============================================================================ */
(function () {
  "use strict";

  /* ---------------------------------------------------------------------
     TRAP H — leak control.
     The theme editor injects and removes sections live. Without teardown,
     every edit stacks another copy of every timeline on top of the last.

     Attribution note, stated plainly: this theme's motion system is
     genuinely cross-section by design — one Lenis, one world colour ramp,
     ScrollTriggers whose trigger and target live in different sections, and
     a pin on #firsthour whose spacing depends on total document height. Most
     triggers cannot be attributed to a single section, so a surgical
     per-section kill would leave the cross-section ones stale.

     What this does instead: record EVERYTHING boot() creates, and on any
     section load or unload tear the whole motion system down and rebuild it.
     That is a superset of the per-section requirement — no duplicates and no
     stale triggers — at the cost of a rebuild rather than a partial one.
     Section-LOCAL listeners (faq, map, doors) are additionally tracked by
     section id so a removed section cannot leave a dangling handler.
     --------------------------------------------------------------------- */
  var REG = {
    triggers: [],
    tweens: [],
    intervals: [],
    matchMedias: [],
    listeners: [],
    bySection: Object.create(null)
  };

  var booted = false;

  function sectionIdOf(el) {
    if (!el || !el.closest) return null;
    var w = el.closest('.shopify-section');
    return w ? w.id : null;
  }

  function remember(kind, item, el) {
    REG[kind].push(item);
    var id = sectionIdOf(el);
    if (!id) return;
    (REG.bySection[id] = REG.bySection[id] || []).push({ kind: kind, item: item });
  }

  function teardown() {
    if (window.ScrollTrigger) {
      try { ScrollTrigger.getAll().forEach(function (t) { t.kill(); }); } catch (e) {}
    }
    REG.tweens.forEach(function (t) { try { t.kill(); } catch (e) {} });
    REG.intervals.forEach(function (i) { clearInterval(i); });
    REG.matchMedias.forEach(function (m) { try { m.revert(); } catch (e) {} });
    REG.listeners.forEach(function (l) {
      try { l.target.removeEventListener(l.type, l.fn, l.opts); } catch (e) {}
    });
    REG.triggers.length = 0;
    REG.tweens.length = 0;
    REG.intervals.length = 0;
    REG.matchMedias.length = 0;
    REG.listeners.length = 0;
    REG.bySection = Object.create(null);
  }

  /* =====================================================================
     ORIGINAL MOTION SYSTEM — was templates/index.liquid lines 836-1188.
     Body unmodified: the scroll sync, the pin, the reduced-motion branches
     and every selector are exactly as they were.
     ===================================================================== */
  function buildApp() {

  "use strict";
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion:reduce)').matches;
  var hasGSAP = typeof window.gsap !== 'undefined';
  var hasST = hasGSAP && typeof window.ScrollTrigger !== 'undefined';
  var hasSplit = hasGSAP && typeof window.SplitText !== 'undefined';
  var $ = function(s,c){return (c||document).querySelector(s);};
  var $$ = function(s,c){return Array.prototype.slice.call((c||document).querySelectorAll(s));};
  var lerp = function(a,b,t){return a+(b-a)*t;};
  var clamp = function(v,a,b){return v<a?a:(v>b?b:v);};

  /* ─────────── DOORS COUNTDOWN LOGIC ───────────
     Door days: Thursday(4), Friday(5), Saturday(6) at 19:00 local.
     Close: Thu +6h (1AM), Fri +7h (2AM), Sat +7h (2AM). */
  var DOOR_DAYS = [4,5,6];
  var DOOR_HOUR = 19;
  function nextDoors(now){
    for(var add=0; add<=7; add++){
      var d = new Date(now.getFullYear(), now.getMonth(), now.getDate()+add, DOOR_HOUR, 0, 0, 0);
      if(DOOR_DAYS.indexOf(d.getDay()) !== -1 && d.getTime() > now.getTime()) return d;
    }
    return null;
  }
  function serviceState(now){
    var spec = [{d:4,close:6},{d:5,close:7},{d:6,close:7}];
    for(var i=0;i<spec.length;i++){
      for(var back=0;back<=1;back++){
        var s = new Date(now.getFullYear(), now.getMonth(), now.getDate()-back, DOOR_HOUR, 0, 0, 0);
        if(s.getDay() === spec[i].d){
          var e = new Date(s.getTime() + spec[i].close*3600*1000);
          if(now.getTime() >= s.getTime() && now.getTime() < e.getTime()) return {open:true, close:e};
        }
      }
    }
    return {open:false};
  }
  var DAYNAMES = ['SUNDAY','MONDAY','TUESDAY','WEDNESDAY','THURSDAY','FRIDAY','SATURDAY'];
  function pad(n){return (n<10?'0':'')+n;}

  var el = {
    cd: $('#cd'), cdLabel: $('#cdLabel'), cdWhen: $('#cdWhen'), cdClock: $('#cdClock'),
    cdD: $('#cdD'), cdH: $('#cdH'), cdM: $('#cdM'), cdS: $('#cdS'),
    resD: $('#resD'), resH: $('#resH'), resM: $('#resM'), resS: $('#resS'), resLabel: $('#resLabel'),
    chipTxt: $('#chipTxt')
  };
  function tickCountdown(){
    var now = new Date();
    var svc = serviceState(now);
    if(svc.open){
      var mins = Math.max(0, Math.round((svc.close.getTime()-now.getTime())/60000));
      if(el.cd) el.cd.classList.add('open');
      if(el.cdLabel) el.cdLabel.textContent = 'DOORS ARE OPEN';
      if(el.cdWhen) el.cdWhen.textContent = 'WE ARE SERVING';
      if(el.cdD) el.cdD.textContent = '00';
      if(el.cdH) el.cdH.textContent = '00';
      if(el.cdM) el.cdM.textContent = pad(Math.floor(mins/60));
      if(el.cdS) el.cdS.textContent = pad(mins%60);
      if(el.chipTxt) el.chipTxt.textContent = 'DOORS OPEN NOW';
      if(el.resLabel) el.resLabel.textContent = 'OPEN · LAST CALL IN';
      if(el.resD) el.resD.textContent = '00';
      if(el.resH) el.resH.textContent = pad(Math.floor(mins/60));
      if(el.resM) el.resM.textContent = pad(mins%60);
      if(el.resS) el.resS.textContent = '00';
      return;
    }
    if(el.cd) el.cd.classList.remove('open');
    if(el.cdLabel) el.cdLabel.textContent = 'NEXT DOORS';
    var nd = nextDoors(now);
    if(!nd) return;
    var diff = Math.max(0, nd.getTime()-now.getTime());
    var s = Math.floor(diff/1000);
    var d = Math.floor(s/86400); s -= d*86400;
    var h = Math.floor(s/3600); s -= h*3600;
    var m = Math.floor(s/60); s -= m*60;
    if(el.cdWhen) el.cdWhen.textContent = DAYNAMES[nd.getDay()] + ' · 7:00 PM';
    if(el.cdD) el.cdD.textContent = pad(d);
    if(el.cdH) el.cdH.textContent = pad(h);
    if(el.cdM) el.cdM.textContent = pad(m);
    if(el.cdS) el.cdS.textContent = pad(s);
    if(el.resD) el.resD.textContent = pad(d);
    if(el.resH) el.resH.textContent = pad(h);
    if(el.resM) el.resM.textContent = pad(m);
    if(el.resS) el.resS.textContent = pad(s);
    if(el.resLabel) el.resLabel.textContent = 'NEXT DOORS IN';
    var chip = d>0 ? ('DOORS IN '+d+'D '+pad(h)+'H') : (h>0 ? ('DOORS IN '+h+'H '+pad(m)+'M') : ('DOORS IN '+m+'M '+pad(s)+'S'));
    if(el.chipTxt) el.chipTxt.textContent = chip;
  }
  tickCountdown();
  setInterval(tickCountdown, 1000);

  /* ─────────── PRELOADER (<= 2.5s) ─────────── */
  var pre = $('#pre'), preNum = $('#preNum'), preBar = $('#pre .pre-bar i'), preLetters = $$('#pre .pre-word span');
  function hidePre(){ if(pre){ pre.classList.add('gone'); } if(hasST){ setTimeout(function(){ ScrollTrigger.refresh(); }, 60); } }
  var preDone = false;
  function finishPre(){ if(preDone) return; preDone = true; hidePre(); startHero(); }
  if(hasGSAP && !reduce){
    var counter = {v:0};
    var tl = gsap.timeline({onComplete:finishPre});
    /* Original intro timing — do not shorten. The splash is the brand moment;
       the booking widget is synced to it instead (alia-widget.js reveals the
       launcher the instant #pre gains .gone, with no added delay). */
    tl.to(preLetters, {yPercent:-115, opacity:1, stagger:0.09, duration:0.6, ease:'power3.out'}, 0.05)
      .to(preBar, {scaleX:1, duration:1.15, ease:'power2.inOut'}, 0.1)
      .to(counter, {v:100, duration:1.15, ease:'power2.inOut', onUpdate:function(){ if(preNum) preNum.textContent = Math.round(counter.v); }}, 0.1)
      .to('#pre .pre-word span', {yPercent:-230, opacity:0, stagger:0.05, duration:0.4, ease:'power2.in'}, 1.55)
      .to('#pre .pre-bar, #pre .pre-meta', {opacity:0, duration:0.3}, 1.55);
  } else {
    if(preNum) preNum.textContent = '100';
    preLetters.forEach(function(s){ s.style.transform='none'; s.style.opacity='1'; });
    setTimeout(finishPre, 300);
  }
  setTimeout(function(){ finishPre(); }, 2500); // hard cap safety

  /* ─────────── CUSTOM CURSOR ─────────── */
  if(!reduce && window.matchMedia('(hover:hover)').matches){
    var cur = $('#cursor'), dot = $('#cursorDot');
    var mx=innerWidth/2, my=innerHeight/2, cx=mx, cy=my;
    window.addEventListener('mousemove', function(e){ mx=e.clientX; my=e.clientY; if(dot){ dot.style.transform='translate('+mx+'px,'+my+'px)'; } }, {passive:true});
    (function loop(){ cx=lerp(cx,mx,0.18); cy=lerp(cy,my,0.18); if(cur){ cur.style.transform='translate('+cx+'px,'+cy+'px)'; } requestAnimationFrame(loop); })();
    var big = 'a,button,.mi,.faq-q,.btn,.brand,#navChip';
    document.addEventListener('mouseover', function(e){ if(e.target.closest && e.target.closest(big)) document.body.classList.add('cur-lg'); });
    document.addEventListener('mouseout', function(e){ if(e.target.closest && e.target.closest(big)) document.body.classList.remove('cur-lg'); });
  }

  /* ─────────── BUILD ROOM (settings + candles) ─────────── */
  var settingsG = $('#settings'), candlesG = $('#candles');
  var SVGNS = 'http://www.w3.org/2000/svg';
  function mk(tag, attrs){ var n=document.createElementNS(SVGNS,tag); for(var k in attrs){ n.setAttribute(k, attrs[k]); } return n; }
  var seatX = [280, 410, 540, 670, 800];
  var seatY = 452;
  seatX.forEach(function(x,i){
    var g = mk('g', {'class':'setting'});
    g.appendChild(mk('ellipse', {'class':'draw','cx':x,'cy':seatY,'rx':34,'ry':13}));         // plate
    g.appendChild(mk('ellipse', {'class':'draw','cx':x,'cy':seatY,'rx':20,'ry':7}));           // inner plate
    g.appendChild(mk('line', {'class':'draw','x1':x-52,'y1':seatY-8,'x2':x-52,'y2':seatY+8}));  // fork
    g.appendChild(mk('line', {'class':'draw','x1':x+52,'y1':seatY-8,'x2':x+52,'y2':seatY+8}));  // knife
    g.appendChild(mk('path', {'class':'draw','d':'M'+(x+40)+' '+(seatY-22)+' q6 8 0 16 q-6 -8 0 -16 z'})); // glass bowl
    g.appendChild(mk('line', {'class':'draw','x1':x+40,'y1':seatY-6,'x2':x+40,'y2':seatY+4})); // glass stem
    settingsG.appendChild(g);
  });
  var candX = [280, 410, 540, 670, 800];
  var candTop = 292;
  candX.forEach(function(x,i){
    var g = mk('g', {'class':'cand','data-i':i});
    g.appendChild(mk('line', {'class':'wick','x1':x,'y1':candTop+6,'x2':x,'y2':candTop-4}));   // wick
    g.appendChild(mk('circle', {'class':'halo','cx':x,'cy':candTop-14,'r':22}));               // glow
    g.appendChild(mk('path', {'class':'flame','d':'M'+x+' '+(candTop-30)+' q9 12 0 26 q-9 -14 0 -26 z'})); // flame
    g.appendChild(mk('rect', {'class':'cbody','x':x-5,'y':candTop+6,'width':10,'height':40,'rx':2})); // candle body
    candlesG.appendChild(g);
  });

  /* ─────────── LENIS + SCROLLTRIGGER ─────────── */
  var lenis = null;
  if(hasGSAP && hasST){
    gsap.registerPlugin(ScrollTrigger);
    if(hasSplit){ try{ gsap.registerPlugin(SplitText); }catch(e){} }
    if(typeof Lenis !== 'undefined' && !reduce){
      try{
        lenis = new Lenis({ lerp:0.09, wheelMultiplier:1, smoothWheel:true });
        lenis.on('scroll', function(){ ScrollTrigger.update(); });
        gsap.ticker.add(function(t){ lenis.raf(t*1000); });
        gsap.ticker.lagSmoothing(0);
        window.__lenis = lenis;
      }catch(e){ lenis = null; }
    }
  }

  /* ─────────── HERO INTRO ─────────── */
  function startHero(){
    if(!hasGSAP || reduce){ return; }
    var htl = gsap.timeline();
    htl.from('.hero-title .hl span', {yPercent:118, duration:1.05, stagger:0.12, ease:'power4.out'}, 0)
       .from('.hero-top .reveal', {y:18, opacity:0, duration:0.7, stagger:0.1, ease:'power2.out'}, 0.15)
       .from('.hero-sub', {y:22, opacity:0, duration:0.8, ease:'power2.out'}, 0.5)
       .from('#cd', {y:26, opacity:0, duration:0.9, ease:'power3.out'}, 0.6)
       .from('.alia-doors', {y:24, opacity:0, duration:0.85, ease:'power3.out'}, 0.75)
       .from('.scrollcue', {opacity:0, duration:0.7, ease:'power2.out'}, 0.9);
  }

  if(hasGSAP && hasST && !reduce){
    /* ── world light driver (day to night) ── */
    var world = $('#world');
    var RAMP = [
      {p:0.00, c:[234,233,220]}, // parchment
      {p:0.30, c:[138,95,58]},   // amber
      {p:0.55, c:[74,51,31]},    // deep roast
      {p:0.78, c:[36,26,17]},    // char
      {p:1.00, c:[36,26,17]}
    ];
    function rampColor(p){
      for(var i=0;i<RAMP.length-1;i++){
        var a=RAMP[i], b=RAMP[i+1];
        if(p>=a.p && p<=b.p){
          var t=(p-a.p)/(b.p-a.p);
          return 'rgb('+Math.round(lerp(a.c[0],b.c[0],t))+','+Math.round(lerp(a.c[1],b.c[1],t))+','+Math.round(lerp(a.c[2],b.c[2],t))+')';
        }
      }
      return 'rgb(36,26,17)';
    }
    ScrollTrigger.create({
      trigger:'body', start:'top top', end:'bottom bottom', scrub:true,
      onUpdate:function(self){
        var p = self.progress;
        if(world) world.style.backgroundColor = rampColor(p);
        var sunY = lerp(14, 96, clamp(p/0.56,0,1));
        var sunO = 1 - clamp((p-0.42)/0.2, 0, 1);
        var candO = clamp((p-0.44)/0.34, 0, 1) * 0.9;
        var root = document.documentElement.style;
        root.setProperty('--sun-y', sunY+'vh');
        root.setProperty('--sun-o', sunO.toFixed(3));
        root.setProperty('--cand-o', candO.toFixed(3));
      }
    });

    /* ── nav shrink + chip ── */
    var nav = $('#nav'), chip = $('#navChip');
    ScrollTrigger.create({ trigger:'#hero', start:'bottom 80%', onEnter:function(){ nav.classList.add('shrunk'); if(chip) chip.classList.add('on'); }, onLeaveBack:function(){ nav.classList.remove('shrunk'); if(chip) chip.classList.remove('on'); } });

    /* ── progress bar ── */
    var pbar = $('#progress');
    ScrollTrigger.create({ trigger:'body', start:'top top', end:'bottom bottom', scrub:true, onUpdate:function(s){ if(pbar) pbar.style.width = (s.progress*100)+'%'; } });

    /* ── generic reveals ── */
    $$('.reveal').forEach(function(node){
      if(node.closest('#hero')) return;
      gsap.from(node, { y:34, opacity:0, duration:0.9, ease:'power3.out',
        scrollTrigger:{ trigger:node, start:'top 88%' } });
    });

    /* ── headline splits ── */
    if(hasSplit){
      $$('h2').forEach(function(h){
        if(h.closest('#hero')) return;
        try{
          var sp = new SplitText(h, {type:'lines', linesClass:'sl-line'});
          gsap.set(sp.lines, {overflow:'hidden'});
          gsap.from(sp.lines, { yPercent:110, duration:1, stagger:0.12, ease:'power4.out',
            scrollTrigger:{ trigger:h, start:'top 86%' } });
        }catch(e){}
      });
      var q = $('.quote');
      if(q){ try{
        var qs = new SplitText(q, {type:'words', wordsClass:'word'});
        $$('.quote .word').forEach(function(w){ var inner=document.createElement('span'); while(w.firstChild){ inner.appendChild(w.firstChild); } w.appendChild(inner); });
        gsap.from('.quote .word span', { yPercent:120, duration:1, stagger:0.09, ease:'power4.out',
          scrollTrigger:{ trigger:q, start:'top 82%' } });
      }catch(e){} }
    }

    /* ── THE FIRST HOUR pinned sequence ── */
    var mm = gsap.matchMedia();
    mm.add('(min-width:821px)', function(){
      var draws = $$('#roomSvg .draw');
      draws.forEach(function(pth){ try{ var L=pth.getTotalLength(); pth.style.strokeDasharray=L; pth.style.strokeDashoffset=L; }catch(e){} });
      var floorD = $$('#roomSvg .floor');
      var tableD = $$('#roomSvg .table');
      var setD = $$('#settings .draw');
      var cands = $$('#roomSvg .cand');
      gsap.set(cands, {opacity:0});
      var fhTime=$('#fhTime'), fhCand=$('#fhCand'), fhSet=$('#fhSet'), fhCourse=$('#fhCourse');
      var courses = [
        {k:'The room is empty.', d:'Nothing lit yet. The night has not started.', t:'6:00 PM'},
        {k:'Ovens on.', d:'Kopanisti to the smoke, eggplant to the coals.', t:'6:10 PM'},
        {k:'Candles, one by one.', d:'A small flame down the length of every table.', t:'6:25 PM'},
        {k:'The tables set themselves.', d:'Plates, glass and steel in their places.', t:'6:45 PM'},
        {k:'Doors.', d:'Seven o’clock. The room is warm and full.', t:'7:00 PM'}
      ];
      var curCourse = -1;
      function setCourse(i){ if(i===curCourse||!fhCourse) return; curCourse=i; var c=courses[i]; fhCourse.innerHTML='<span class="fc-k">'+c.k+'</span><span class="fc-d">'+c.d+'</span>'; if(fhTime) fhTime.textContent=c.t; }

      var stTl = gsap.timeline({ scrollTrigger:{ trigger:'#firsthour', start:'top top', end:'+=320%', scrub:0.6, pin:'.fh-stage', anticipatePin:1,
        onUpdate:function(self){
          var p=self.progress;
          var candLit = clamp(Math.floor((clamp((p-0.28)/0.34,0,1))*5+0.001),0,5);
          var setDone = clamp(Math.floor((clamp((p-0.6)/0.34,0,1))*5+0.001),0,5);
          if(fhCand) fhCand.textContent = candLit+' / 5';
          if(fhSet) fhSet.textContent = setDone+' / 5';
          if(p<0.06) setCourse(0); else if(p<0.28) setCourse(1); else if(p<0.6) setCourse(2); else if(p<0.86) setCourse(3); else setCourse(4);
        }
      }});
      stTl.to(floorD, {strokeDashoffset:0, duration:1.2, stagger:0.1, ease:'none'}, 0)
          .to(tableD, {strokeDashoffset:0, duration:1.6, stagger:0.15, ease:'power1.inOut'}, 0.3)
          .to(cands, {opacity:1, duration:0.5, stagger:0.5, ease:'power2.out'}, 1.6)
          .to(setD, {strokeDashoffset:0, duration:1.4, stagger:0.06, ease:'power1.inOut'}, 3.4)
          .to('.fh-title', {letterSpacing:'0.09em', duration:1}, 3.4);
      // light the candle flames progressively via class toggle
      cands.forEach(function(cn, idx){
        ScrollTrigger.create({ trigger:'#firsthour', start:'top top', end:'+=320%', scrub:0.6,
          onUpdate:function(self){ var thresh = 0.28 + idx*0.068; if(self.progress>=thresh) cn.classList.add('lit'); else cn.classList.remove('lit'); } });
      });
      return function(){ /* cleanup handled by matchMedia st.kill */ };
    });

    /* mobile fallback: static lit room */
    mm.add('(max-width:820px)', function(){
      $$('#roomSvg .draw').forEach(function(p){ p.style.strokeDashoffset=0; });
      $$('#roomSvg .cand').forEach(function(c){ c.style.opacity=1; c.classList.add('lit'); });
      var fhCand=$('#fhCand'), fhSet=$('#fhSet'), fhTime=$('#fhTime'), fhCourse=$('#fhCourse');
      if(fhCand) fhCand.textContent='5 / 5'; if(fhSet) fhSet.textContent='5 / 5'; if(fhTime) fhTime.textContent='7:00 PM';
      if(fhCourse) fhCourse.innerHTML='<span class="fc-k">Doors.</span><span class="fc-d">Seven o’clock. The room is warm and full.</span>';
      return function(){};
    });

    /* ── footer marquee drift ── */
    var track = $('.foot-marquee .track');
    if(track){ gsap.to(track, { xPercent:-50, ease:'none', scrollTrigger:{ trigger:'#footer', start:'top bottom', end:'bottom top', scrub:1 } }); }

  } else {
    /* No GSAP or reduced motion: reveal everything, static lit room */
    $$('.reveal').forEach(function(n){ n.style.opacity=1; });
    $$('.hero-title .hl span').forEach(function(n){ n.style.transform='none'; });
    $$('#roomSvg .draw').forEach(function(p){ p.style.strokeDashoffset=0; });
    $$('#roomSvg .cand').forEach(function(c){ c.style.opacity=1; });
    var nav2=$('#nav'), chip2=$('#navChip');
    window.addEventListener('scroll', function(){ if(window.scrollY>window.innerHeight*0.7){ nav2.classList.add('shrunk'); if(chip2) chip2.classList.add('on'); } else { nav2.classList.remove('shrunk'); if(chip2) chip2.classList.remove('on'); } }, {passive:true});
  }

  /* ─────────── SMOOTH ANCHORS ─────────── */
  $$('a[href^="#"]').forEach(function(a){
    a.addEventListener('click', function(e){
      var id=a.getAttribute('href'); if(id.length<2) return;
      var t=document.querySelector(id); if(!t) return;
      e.preventDefault();
      if(window.__lenis && window.__lenis.scrollTo){ window.__lenis.scrollTo(t, {offset:-10}); }
      else { t.scrollIntoView({behavior:reduce?'auto':'smooth'}); }
    });
  });

  /* ─────────── FAQ ─────────── */
  $$('.faq-q').forEach(function(q){
    q.addEventListener('click', function(){
      var item=q.closest('.faq-item'); var a=item.querySelector('.faq-a');
      var isOpen=item.classList.contains('open');
      $$('.faq-item.open').forEach(function(o){ o.classList.remove('open'); o.querySelector('.faq-a').style.maxHeight=null; });
      if(!isOpen){ item.classList.add('open'); a.style.maxHeight=a.scrollHeight+'px'; }
    });
  });

  /* ─────────── EASTER EGG: press 7 ─────────── */
  window.addEventListener('keydown', function(e){
    if(e.key==='7'){
      var fl=document.createElement('div');
      fl.style.cssText='position:fixed;inset:0;z-index:9995;pointer-events:none;background:radial-gradient(circle at 50% 60%,rgba(138,95,58,.55),rgba(36,26,17,0) 60%);opacity:0';
      document.body.appendChild(fl);
      if(hasGSAP && !reduce){ gsap.to(fl,{opacity:1,duration:0.3,ease:'power2.out',onComplete:function(){ gsap.to(fl,{opacity:0,duration:1.1,ease:'power2.in',onComplete:function(){ fl.remove(); }}); }}); }
      else { fl.style.opacity='1'; setTimeout(function(){ fl.remove(); }, 400); }
      $$('#roomSvg .cand').forEach(function(c){ c.classList.add('lit'); });
    }
  });

  /* fonts ready refresh */
  if(document.fonts && document.fonts.ready){ document.fonts.ready.then(function(){ if(hasST) ScrollTrigger.refresh(); }); }
  window.addEventListener('load', function(){ if(hasST) setTimeout(function(){ ScrollTrigger.refresh(); }, 120); });

  }

  /* =====================================================================
     ALIA-DOCK-REVEAL — was lines 1215-1236.
     ===================================================================== */
  function buildDock() {

    function run(){
      var d=document.querySelector('.alia-cta-dock'); if(!d) return;
      function reveal(){ d.classList.add('revealed'); }
      var SEL='#preloader,#intro,#introStatement,[id*="preload" i],[class*="preloader" i],[class*="intro-idx"],[class*="intro-col"],[class*="intro-grid"],.intro-statement,.d-veil,.hero-veil,.tex-veil,.pre-veil,[class*="veil"]';
      var pre=document.querySelector(SEL);
      if(pre){
        var start=Date.now();
        var iv=setInterval(function(){
          var el=document.querySelector(SEL), gone=!el;
          if(el){ var cs=getComputedStyle(el); gone=(cs.display==='none'||cs.visibility==='hidden'||parseFloat(cs.opacity)<0.1); }
          if(gone || (Date.now()-start)>6000){ reveal(); clearInterval(iv); }
        },200);
      } else {
        if(document.readyState==='complete'){ setTimeout(reveal,700); }
        else { window.addEventListener('load',function(){ setTimeout(reveal,700); }); }
      }
    }
    if(document.readyState==='loading'){ document.addEventListener('DOMContentLoaded',run); } else { run(); }
  
  }

  /* =====================================================================
     ALIA-MAP-LIVE — was lines 1267-1290.
     ===================================================================== */
  function buildMap() {

    var wrap = document.querySelector('[data-map]');
    if(!wrap) return;
    var btn = wrap.querySelector('[data-map-open]');
    if(!btn) return;
    btn.addEventListener('click', function(){
      var f = document.createElement('iframe');
      /* ?output=embed needs no API key and no billing account. */
      f.src = 'https://www.google.com/maps?q=' +
        encodeURIComponent('997 Rue Saint-Jacques, Montreal, QC H3C 1G6') +
        '&z=16&output=embed';
      f.title = 'Map of ALIA Montreal, 997 Rue Saint-Jacques';
      f.loading = 'lazy';
      f.referrerPolicy = 'no-referrer-when-downgrade';
      f.setAttribute('allowfullscreen','');
      var svg = wrap.querySelector('svg');
      if(svg) svg.remove();
      btn.remove();
      wrap.classList.add('is-live');
      wrap.appendChild(f);
    });
  
  }

  /* ---------------------------------------------------------------------
     BOOT
     --------------------------------------------------------------------- */
  /* =====================================================================
     MOBILE DRAWER
     ---------------------------------------------------------------------
     Below 820px the theme hides .nav-links and shipped no hamburger, so the
     menu was unreachable on a phone.

     Every listener that is NOT on a node inside the nav section is recorded in
     REG.listeners, because the nav's own nodes are replaced wholesale when the
     editor reloads the section — their listeners die with them — while
     document-level ones would otherwise stack on every reload (trap H).
     ===================================================================== */
  function buildDrawer() {
    var burger = document.querySelector('[data-drawer-toggle]');
    var drawer = document.querySelector('[data-drawer]');
    var scrim  = document.querySelector('[data-drawer-scrim]');
    var closeB = document.querySelector('[data-drawer-close]');
    if (!burger || !drawer || !scrim) return;

    var lastFocus = null;
    var priorOverflow = '';

    function isOpen() { return drawer.classList.contains('is-open'); }

    function setOpen(open) {
      drawer.classList.toggle('is-open', open);
      scrim.classList.toggle('is-open', open);
      burger.setAttribute('aria-expanded', String(open));
      /* freeze the page behind the drawer, and put the scrollbar's width back
         as padding so the layout does not jump sideways */
      if (open) {
        lastFocus = document.activeElement;
        priorOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        var first = drawer.querySelector('a, button');
        if (first) first.focus({ preventScroll: true });
      } else {
        document.body.style.overflow = priorOverflow;
        if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
      }
    }

    burger.addEventListener('click', function () { setOpen(!isOpen()); });
    scrim.addEventListener('click', function () { setOpen(false); });
    if (closeB) closeB.addEventListener('click', function () { setOpen(false); });

    /* Any link inside the drawer closes it. An in-page anchor would otherwise
       scroll the page behind a drawer that is still covering it. */
    Array.prototype.forEach.call(drawer.querySelectorAll('a'), function (a) {
      a.addEventListener('click', function () { setOpen(false); });
    });
    /* Corporate opens the lead form; the drawer must get out of its way. */
    Array.prototype.forEach.call(drawer.querySelectorAll('[data-act="corp"]'), function (b) {
      b.addEventListener('click', function () { setOpen(false); });
    });

    function onKey(e) { if (e.key === 'Escape' && isOpen()) setOpen(false); }
    document.addEventListener('keydown', onKey);
    remember('listeners', { target: document, type: 'keydown', fn: onKey }, burger);

    /* Crossing the breakpoint with the drawer open would leave the page
       scroll-locked with nothing visible holding it. */
    var mq = window.matchMedia('(min-width: 821px)');
    function onWide(e) { if (e.matches && isOpen()) setOpen(false); }
    if (mq.addEventListener) {
      mq.addEventListener('change', onWide);
      remember('listeners', { target: mq, type: 'change', fn: onWide }, burger);
    }
  }

  function boot() {
    buildApp();
    buildDock();
    buildMap();
    buildDrawer();
    booted = true;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  /* ---------------------------------------------------------------------
     SHOPIFY SECTION LIFECYCLE

     Requirement 4: refresh ScrollTrigger after the editor injects or moves a
     section, or the pin on #firsthour computes its spacing against a stale
     document height and the layout jumps.

     Rebuilds are debounced because the editor fires several section events in
     a burst when a section is reordered.
     --------------------------------------------------------------------- */
  var rebuildTimer = null;
  function scheduleRebuild() {
    clearTimeout(rebuildTimer);
    rebuildTimer = setTimeout(function () {
      teardown();
      boot();
      if (window.ScrollTrigger) ScrollTrigger.refresh();
    }, 60);
  }

  document.addEventListener('shopify:section:load', function (e) {
    /* TRAP E — the preloader must not re-run on section reload.
       If the editor re-injects the preloader section after the page has
       already booted, a full-screen overlay would reappear over the site.
       Dismiss it on sight. */
    if (booted) {
      var pre = (e.target && e.target.querySelector) ? e.target.querySelector('#pre') : null;
      if (pre) { pre.classList.add('gone'); pre.style.display = 'none'; }
    }
    scheduleRebuild();
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var id = e.target && e.target.id;
    var owned = id && REG.bySection[id];
    if (owned) {
      owned.forEach(function (r) {
        if (r.kind === 'intervals') clearInterval(r.item);
        else if (r.kind === 'listeners') {
          try { r.item.target.removeEventListener(r.item.type, r.item.fn, r.item.opts); } catch (err) {}
        } else { try { r.item.kill(); } catch (err) {} }
      });
      delete REG.bySection[id];
    }
    scheduleRebuild();
  });

  /* the editor also fires these for blocks */
  document.addEventListener('shopify:block:select', function () {
    if (window.ScrollTrigger) ScrollTrigger.refresh();
  });
  document.addEventListener('shopify:block:deselect', function () {
    if (window.ScrollTrigger) ScrollTrigger.refresh();
  });
})();
